import PropTypes from "prop-types";
import { Alert, AlertTitle, Box, Link, Stack, Typography } from "@mui/material";
import { IconCreditCard, IconMail, IconPhone, IconPhoneCheck, IconReceipt } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import { CopyButton } from "./Answers";
import { dayOf, telHref, timeWords, urgentSteps } from "./decisions";
import { travellerProgress } from "./rules";
import { reveal } from "./workspace";
import { money } from "./format";
import { hasVisa, paymentArranged } from "./workflow";

// What the agent does next on this case, always in one place, with the one
// button that does it. The words go above the button, never squeezed beside
// it. Most urgent first (API docs/visa-decisions.md): a code the customer
// just sent, the call after a refusal, a fee receipt to check, then the
// case's usual next step.
export function nextSteps(data, visas, options = {}) {
  if (data.status === "incomplete") return [];
  const usual = usualStep(data, visas);
  return [...urgentSteps(data, options), ...(usual ? [usual] : [])];
}

export const nextStep = (data, visas, options) => nextSteps(data, visas, options)[0] || null;

function usualStep(data, visas) {
  const progress = data.travellers.map(travellerProgress);
  const toReview = progress.reduce((n, p) => n + p.review.toReview, 0);
  const missing = progress.reduce((n, p) => n + p.documents.missing, 0);
  const newCopies = progress.reduce((n, p) => n + p.review.newCopy, 0);
  const uploads = progress.reduce((n, p) => n + p.review.uploads, 0);
  const allApproved = uploads > 0 && !toReview && !newCopies && !missing;
  const unpaid = !paymentArranged(data);

  if (data.status === "pending") {
    if (unpaid) return { kind: "unpaid" };
    if (toReview) return { kind: "review", count: toReview };
    if (allApproved) return { kind: "file" };
    return null;
  }
  if (data.status === "under_review") return { kind: "approve", unpaid };
  const known = data.travellers.every((t) => t.visa) || !visas.loading;
  if (data.status === "approved" && known) {
    const without = data.travellers.filter((t) => !hasVisa(t, visas.byId[t.request_id]));
    if (without.length) return { kind: "send", count: without.length, unpaid };
  }
  return null;
}

// The one button the case's sticky header shows for this step, so the
// screen's main button is the next step, not "Change status" (QA nadia-21).
export function primaryAction(step, { onStatus, onDecide, onAddVisa, onReview, onCalled, onReceipt }) {
  if (!step) return null;
  switch (step.kind) {
    case "review":
      return { label: step.count === 1 ? "Review the document" : "Review the first document", onClick: onReview };
    case "file":
      return { label: "Set to In review", onClick: () => onStatus("under_review") };
    case "approve":
      return { label: "Record the decision", onClick: onDecide };
    case "send":
      return { label: "Add the visa", onClick: onAddVisa };
    case "unpaid":
      // It only scrolls to the payment: it never takes one (georges-16).
      return { label: "See the payment", onClick: () => reveal("case-payment") };
    case "call":
      return { label: "Mark as called", onClick: onCalled, busyLabel: "Saving…" };
    case "receipt":
      return step.fee?.receipt ? { label: "View receipt", onClick: () => onReceipt?.(step.fee) } : { label: "Go to the fee", onClick: () => reveal(`case-fee-${step.fee?.id}`) };
    default:
      return null;
  }
}

// unpaid: the amount still owed (a number) on an application that isn't
// paid, said in the box whatever the step (QA nadia-04, nadia-22).
export default function NextStep({ step, travellers, partner, customer, canEdit, unpaid = null, onStatus, onDecide, onAddVisa, onReview, onCalled, onReceipt }) {
  if (!step || !canEdit || step.kind === "code") return null;
  const many = travellers > 1;
  const payment = (
    <ActionButton startIcon={<IconCreditCard size={16} />} onClick={() => reveal("case-payment")}>
      See the payment
    </ActionButton>
  );

  const content = {
    // After a refusal a person on the team calls: the customer's page and
    // email promise it.
    call: {
      severity: "warning",
      icon: <IconPhone size={22} />,
      title: step.title || "Call the customer about the refusal",
      text: (
        <>
          {step.phone ? (
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, my: 0.5 }}>
              <Link
                href={telHref(step.phone)}
                underline="hover"
                sx={{ fontSize: "1.125rem", fontWeight: 700, fontFamily: "monospace", color: "inherit", display: "inline-flex", alignItems: "center", gap: 0.75 }}
              >
                {step.phone}
              </Link>
              <CopyButton value={step.phone} label="phone number" />
              {customer ? (
                <Typography variant="body2" component="span">
                  {customer}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Typography variant="body2" fontWeight={600} my={0.5}>
              No phone number on the application: email the customer to ask for one.
            </Typography>
          )}
          Go through the reasons on the letter and what they can do next.
          {step.appeal ? ` They can appeal until ${dayOf(step.appeal)}.` : ""}
        </>
      ),
      actions: (
        <ActionButton variant="contained" startIcon={<IconPhoneCheck size={16} />} busyLabel="Saving…" onClick={onCalled}>
          Mark as called
        </ActionButton>
      ),
    },
    receipt: {
      severity: "info",
      icon: <IconReceipt size={22} />,
      title: step.title || "Check the receipt and tick the fee",
      text: `${step.fee?.title || "A fee"}: the customer says they paid${step.fee?.customer_paid_at ? ` (${timeWords(step.fee.customer_paid_at)})` : ""}.${
        step.fee?.receipt ? "" : " They added no receipt: check it another way before you tick it."
      }`,
      actions: step.fee?.receipt ? (
        <ActionButton variant="contained" startIcon={<IconReceipt size={16} />} onClick={() => onReceipt?.(step.fee)}>
          View receipt
        </ActionButton>
      ) : (
        <ActionButton onClick={() => reveal(`case-fee-${step.fee?.id}`)}>Go to the fee</ActionButton>
      ),
    },
    unpaid: {
      severity: "warning",
      title: "Not paid yet: don’t file this application",
      text: `The customer has not paid${unpaid != null ? ` (${money(unpaid)} outstanding)` : ""}. Review the documents if you like, and file it once the payment is in.`,
      actions: payment,
    },
    review: {
      severity: "info",
      title: step.count === 1 ? "Next: review 1 document" : `Next: review ${step.count} documents`,
      text: "Approve each one, or ask the customer for a new copy.",
      actions: <ActionButton onClick={onReview}>{step.count === 1 ? "Review the document" : "Review the first"}</ActionButton>,
    },
    file: {
      severity: "success",
      title: "All documents approved",
      text: partner
        ? `Next: file the application with the embassy, then set it to In review. ${partner} receives the new status.`
        : "Next: file the application with the embassy, then set it to In review. The customer is not emailed for In review.",
      actions: (
        <ActionButton variant="contained" onClick={() => onStatus("under_review")}>
          Set to In review
        </ActionButton>
      ),
    },
    approve: {
      severity: step.unpaid ? "warning" : "info",
      title: step.unpaid ? "In review, not paid yet" : "In review: waiting for the decision",
      text: `When the consulate’s letter comes, record the decision: approved (with ${many ? "the visas" : "the visa"}, now or later) or refused. ${
        partner ? `${partner} receives it; you choose whether they tell the traveller.` : "You choose whether the customer is emailed."
      }${step.unpaid && unpaid == null ? " The application isn’t paid; you will be asked to confirm." : ""}`,
      actions: (
        <ActionButton variant="contained" onClick={onDecide}>
          Record the decision
        </ActionButton>
      ),
    },
    send: {
      severity: "warning",
      title: "Approved: add the visa",
      text:
        step.count === 1 && !many
          ? `The visa isn’t in the ${partner ? "partner’s hands" : "customer’s account"} yet.`
          : `${step.count} ${step.count === 1 ? "traveller has" : "travellers have"} no visa yet.`,
      actions: (
        <ActionButton variant="contained" startIcon={<IconMail size={16} />} onClick={onAddVisa}>
          Add the visa
        </ActionButton>
      ),
    },
  }[step.kind];

  return (
    <Alert
      severity={content.severity}
      icon={content.icon}
      id="case-next-step"
      tabIndex={-1}
      sx={{ outline: "none", scrollMarginTop: 180, "& .MuiAlert-message": { width: "100%" } }}
    >
      <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>{content.title}</AlertTitle>
      <Typography variant="body2" component="div">
        {content.text}
      </Typography>
      {unpaid != null && step.kind !== "unpaid" ? (
        <Typography variant="body2" fontWeight={700} mt={0.75} sx={{ color: (t) => (t.palette.mode === "dark" ? "#F5C26B" : "#8A5A00") }}>
          Not paid: {money(unpaid)} outstanding.
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} mt={1.25} flexWrap="wrap" useFlexGap>
        {content.actions}
      </Stack>
    </Alert>
  );
}

NextStep.propTypes = {
  step: PropTypes.object,
  travellers: PropTypes.number,
  canEdit: PropTypes.bool,
  unpaid: PropTypes.number,
  onStatus: PropTypes.func.isRequired,
  onDecide: PropTypes.func.isRequired,
  onAddVisa: PropTypes.func.isRequired,
  partner: PropTypes.string,
  customer: PropTypes.string,
  onReview: PropTypes.func.isRequired,
  onCalled: PropTypes.func,
  onReceipt: PropTypes.func,
};
